import { useState, useRef } from 'react';
import { EntrepriseSettings } from '../../mocks/entreprise';
import { Commande, statutLabels } from '../../mocks/commandes';
import { VenteTissuRecord } from '../../mocks/ventes';
import { api } from '../../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatPrice } from '../../utils/format';

type FactureData =
  | { type: 'commande'; data: Commande }
  | { type: 'vente'; data: VenteTissuRecord };

interface FactureModalProps {
  factureData: FactureData;
  entreprise: EntrepriseSettings;
  onClose: () => void;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmt(n: number | string, devise = 'FCFA') {
  return formatPrice(n, true);
}

function generateNumero(id: string) {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `FAC-${y}${m}-${id}`;
}

export default function FactureModal({ factureData, entreprise, onClose }: FactureModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const isCommande = factureData.type === 'commande';
  const commande = isCommande ? (factureData.data as Commande) : null;
  const vente = !isCommande ? (factureData.data as VenteTissuRecord) : null;

  // FIX BUG #8: Fallbacks sécurisés pour tous les champs potentiellement null
  const clientNom = (isCommande ? (commande?.client?.nom || 'Client') : (vente?.clientNom || 'Client')) || 'Client';
  const clientTel = (isCommande ? (commande?.client?.telephone || '') : (vente?.clientTelephone || '')) || '';
  const dateDoc   = (isCommande ? (commande?.dateCommande || '') : (vente?.date || '')) || new Date().toISOString();
  const idDoc     = (isCommande ? (commande?.id || 'N/A') : (vente?.id || 'N/A')) || 'N/A';
  const numFacture = generateNumero(String(idDoc));

  const handlePrint = () => {
    window.print();
  };

  const generateBlob = async (): Promise<Blob | null> => {
    if (!printRef.current) return null;
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'application/pdf', 0.95);
        // html2canvas toBlob is actually image/png/jpeg. We need an image blob for PDF.
        // Actually we just need the canvas as image data for jsPDF.
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const handlePDF = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794 
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`${numFacture}.pdf`);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  const handleWhatsAppPDF = async () => {
    if (!printRef.current) return;
    setSharing(true);
    try {
      // 1. Générer le PDF en mémoire
      const element = printRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      const pdfBlob = pdf.output('blob');

      // 2. Upload sur le serveur
      const token = localStorage.getItem('atelier_bassoum_token') || '';
      const uploadRes = await api.uploadFacturePdf(token, pdfBlob, `${numFacture}.pdf`);
      
      if (uploadRes.status === 'success' && uploadRes.url) {
        // 3. Ouvrir WhatsApp avec le lien
        const phone = clientTel.replace(/\s+/g, '');
        const cleanPhone = phone.startsWith('+') ? phone.replace('+', '') : `223${phone}`;
        const message = encodeURIComponent(`Bonjour ${clientNom},\nVoici votre facture pour ${numFacture}.\nVous pouvez la télécharger ici : ${uploadRes.url}\nMerci pour votre confiance !`);
        window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du partage.");
    } finally {
      setSharing(false);
    }
  };

  // Use central proxy helper for the logo URL
  const logoUrl = entreprise?.logoUrl ? api.getProxyUrl(entreprise.logoUrl) : '/icon-192.png';
  console.log("Logo URL used:", logoUrl); // Debug logo

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-auto" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 print:hidden">
          <div>
            <h3 className="font-playfair font-bold text-lg text-gray-900">Facture / Reçu</h3>
            <p className="text-xs text-gray-400">{numFacture}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
                onClick={handleWhatsAppPDF}
                disabled={sharing}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-green-500 text-white hover:bg-green-600 cursor-pointer font-semibold whitespace-nowrap disabled:opacity-70"
                title="Envoyer par WhatsApp"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-whatsapp-line" /></div>
                {sharing ? 'Envoi...' : 'WhatsApp'}
              </button>
            <button
              onClick={handlePrint}
              className="hidden md:flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer font-semibold whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-printer-line" /></div>
              Imprimer
            </button>
            <button
              onClick={handlePDF}
              disabled={downloading}
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-foret-500 text-white hover:bg-foret-600 cursor-pointer font-semibold whitespace-nowrap disabled:opacity-70"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-download-line" /></div>
              {downloading ? 'Export…' : 'PDF'}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500"
              title="Fermer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Invoice content */}
        <div ref={printRef} className="px-4 py-5 md:px-8 md:py-7 bg-white">
          <style>
            {`
              .dynamic-primary-bg { background-color: ${entreprise.couleurPrimaire}; }
              .dynamic-primary-text { color: ${entreprise.couleurPrimaire}; }
              .dynamic-primary-light-bg { background-color: ${entreprise.couleurPrimaire}15; }
              .dynamic-primary-header-bg { background-color: ${entreprise.couleurPrimaire}12; }
            `}
          </style>

          {/* Company header */}
          <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-gray-100">
            <div className="flex items-start gap-3 md:gap-4">
              {logoUrl ? (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="w-full h-full object-cover" 
                    crossOrigin="anonymous"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl flex-shrink-0 flex items-center justify-center text-white text-xl md:text-2xl font-bold dynamic-primary-bg">
                  {entreprise.nom.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="font-playfair font-bold text-base md:text-xl text-gray-900">{entreprise.nom}</h1>
                {entreprise.slogan && <p className="text-[10px] md:text-xs text-gray-400 italic mt-0.5">{entreprise.slogan}</p>}
                <p className="text-[10px] md:text-xs text-gray-500 mt-2">{entreprise.adresse}</p>
                <p className="text-[10px] md:text-xs text-gray-500">{entreprise.telephone}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-block px-3 py-1.5 md:px-4 md:py-2 rounded-xl dynamic-primary-light-bg">
                <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider dynamic-primary-text">{entreprise.headerText}</p>
                <p className="text-sm md:text-lg font-bold text-gray-900 mt-0.5">{numFacture}</p>
              </div>
              <p className="text-[10px] md:text-xs text-gray-500 mt-2">Date : <strong>{formatDate(dateDoc)}</strong></p>
            </div>
          </div>

          {/* Client + doc info */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Client</p>
              <p className="text-sm font-bold text-gray-800">{clientNom}</p>
              <p className="text-xs text-gray-500 mt-0.5">{clientTel}</p>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {isCommande ? 'Détails commande' : 'Détails vente'}
              </p>
              {isCommande && commande && (
                <>
                  <p className="text-xs text-gray-600"><span className="text-gray-400">Modèle :</span> <strong>{commande.modele}</strong></p>
                  <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Statut :</span> <strong>{statutLabels[commande.statut]}</strong></p>
                  {commande.coupeur && <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Coupeur :</span> {commande.coupeur}</p>}
                  {commande.tailleur && <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Tailleur :</span> {commande.tailleur}</p>}
                </>
              )}
              {!isCommande && vente && (
                <p className="text-xs text-gray-600"><span className="text-gray-400">Vendeur :</span> <strong>{vente.vendeur}</strong></p>
              )}
            </div>
          </div>

          {/* Items table */}
          <div className="mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="dynamic-primary-header-bg">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-tl-lg dynamic-primary-text">Désignation</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider px-3 py-2 dynamic-primary-text">Qté</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider px-3 py-2 dynamic-primary-text">P.U.</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-tr-lg dynamic-primary-text">Total</th>
                </tr>
              </thead>
              {/* Items table logic */}
              {isCommande && commande ? (
                <>
                  {commande.articles && Array.isArray(commande.articles) && commande.articles.length > 0 ? (
                    commande.articles.map((art: any, i: number) => {
                      const coutureTotal = Number(art.total_ligne) || 0;
                      const tissusTotal = (art.tissus || []).reduce((acc: number, t: any) => acc + (Number(t.total_ligne) || 0), 0);
                      const totalArt = coutureTotal + tissusTotal;
                      return (
                        <tbody key={i} className="border-b border-gray-100">
                          <tr className="bg-gray-50/50">
                            <td className="px-3 py-2 font-bold text-gray-900" colSpan={3}>
                              {art.type_habit_nom || art.nom} — {art.modele_nom || 'Sur mesure'}
                            </td>
                            <td className="px-3 py-2 text-right font-black text-gray-900">
                              {fmt(totalArt, entreprise.devise)}
                            </td>
                          </tr>
                          {/* Main d'œuvre */}
                          {Number(art.total_ligne) > 0 && (
                            <tr>
                              <td className="px-3 py-1 pl-6 text-xs text-gray-600 italic">└ Main d'œuvre (Couture)</td>
                              <td className="px-3 py-1 text-center text-xs text-gray-500">{art.quantite}</td>
                              <td className="px-3 py-1 text-right text-xs text-gray-500">{fmt(art.prix_unitaire, entreprise.devise)}</td>
                              <td className="px-3 py-1 text-right text-xs text-blue-600 font-bold">{fmt(art.total_ligne, entreprise.devise)}</td>
                            </tr>
                          )}
                          {/* Tissus */}
                          {(art.tissus || []).map((t: any, ti: number) => (
                            <tr key={ti}>
                              <td className="px-3 py-1 pl-6 text-xs text-gray-600 italic text-terre-600">
                                └ {t.is_client_cloth ? 'Tissu client' : 'Tissu boutique'}: {t.tissu_nom || t.description}
                              </td>
                              <td className="px-3 py-1 text-center text-xs text-gray-500">{t.quantite}{t.tissu_unite || 'm'}</td>
                              <td className="px-3 py-1 text-right text-xs text-gray-500">{fmt(t.prix_unitaire || 0, entreprise.devise)}</td>
                              <td className="px-3 py-1 text-right text-xs text-terre-700 font-bold">{fmt(t.total_ligne || 0, entreprise.devise)}</td>
                            </tr>
                          ))}
                        </tbody>
                      );
                    })
                  ) : (
                    <tbody className="border-b border-gray-100">
                      <tr>
                        <td className="px-3 py-2.5 text-gray-800 font-medium">Confection — {commande.modele || 'Tenue sur-mesure'}</td>
                        <td className="px-3 py-2.5 text-center text-gray-600">1</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{fmt(commande.paiement.total, entreprise.devise)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(commande.paiement.total, entreprise.devise)}</td>
                      </tr>
                    </tbody>
                  )}
                </>
              ) : vente ? (
                <tbody className="border-b border-gray-100">
                  {vente.articles && Array.isArray(vente.articles) ? (
                    vente.articles.map((art: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-3 py-2.5 text-gray-800 font-medium">{art.tissuNom || art.tissu_nom}</td>
                        <td className="px-3 py-2.5 text-center text-gray-600">{art.quantite} {art.unite || art.tissu_unite}</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{fmt(art.prixUnitaire || art.prix_unitaire, entreprise.devise)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(art.total || art.total_ligne, entreprise.devise)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-3 py-2.5 text-gray-800 font-medium">{vente.tissu_nom || 'Tissu'}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600">{vente.quantite} {vente.tissu_unite || 'm'}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(vente.prix_unitaire || 0, entreprise.devise)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(vente.total, entreprise.devise)}</td>
                    </tr>
                  )}
                </tbody>
              ) : null}
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-4 md:mb-8">
            <div className="w-full md:w-64 space-y-1.5 bg-gray-50 p-4 rounded-2xl">
              {isCommande && commande ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total brut</span>
                    <span className="font-bold text-gray-900">{fmt(commande.paiement.total, entreprise.devise)}</span>
                  </div>
                  {commande.paiement.remise > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gold-600 font-semibold">Remise</span>
                      <span className="text-gold-600 font-bold">— {fmt(commande.paiement.remise, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(commande.paiement.acompte, entreprise.devise)}</span>
                  </div>
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">Net à payer</span>
                    <span className="font-bold text-gray-900">{fmt(Number(commande.paiement.total || 0) - Number(commande.paiement.remise || 0), entreprise.devise)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 font-semibold italic">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(commande.paiement.acompte, entreprise.devise)}</span>
                  </div>
                  <div className="h-px bg-gray-200 my-1" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">Reste à payer</span>
                    <span className={`font-bold text-lg ${commande.paiement.reste <= 0 ? 'text-green-600' : 'text-red-500'}`}>{fmt(commande.paiement.reste, entreprise.devise)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-50 flex justify-between items-center">
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Mode : {commande.paiement.modePaiement || 'N/A'}</p>
                    {commande.paiement.vendeur && (
                      <p className="text-[10px] text-gray-500 font-bold italic">Signé : {commande.paiement.vendeur}</p>
                    )}
                  </div>
                </>
              ) : vente ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total brut</span>
                    <span className="font-bold text-gray-900">{fmt(vente.total, entreprise.devise)}</span>
                  </div>
                  {(vente.remise ?? 0) > 0 && (
                    <div className="flex justify-between text-sm text-gold-600 font-bold">
                      <span>Remise</span>
                      <span>— {fmt(vente.remise ?? 0, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(vente.acompte ?? vente.montantRecu, entreprise.devise)}</span>
                  </div>
                  {(vente.monnaie ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Monnaie rendue</span>
                      <span className="text-gray-600">{fmt(vente.monnaie ?? 0, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">{(vente.reste ?? 0) > 0 ? 'Reste à payer' : 'Soldé'}</span>
                    <span className={`font-bold text-lg ${(vente.reste ?? 0) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {(vente.reste ?? 0) > 0 ? fmt(vente.reste ?? 0, entreprise.devise) : 'Payé'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 text-right">Mode : {vente.modePaiement}</p>
                </>
              ) : null}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 text-center">
            <p className="text-xs text-gray-400 leading-relaxed">{entreprise.footerText}</p>
            <p className="text-xs text-gray-300 mt-2">{entreprise.nom} · {entreprise.adresse}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
